import React from 'react';
import {
  NumberInput, SegmentedControl, Stack, Switch,
} from '@mantine/core';
import InspectorRow from '../../../puck/fields/InspectorRow';
import { PxSpacingInput } from '../../../puck/fields/puckPxSpacingField';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { useThemeLockedProp } from '../../../puck/fields/themedFieldValues';
import StyleDefaultMark from '../../../puck/fields/StyleDefaultMark';
import { CAROUSEL_SCROLL_DIRECTIONS, CAROUSEL_VISIBLE_MAX } from './carouselLayout';

/**
 * Champ composite « Slides visibles » : un interrupteur au lieu d'un choix de
 * style. La vraie question est « combien de slides à l'écran », et « le
 * maximum » n'est qu'une réponse de plus à cette même question. Le champ édite
 * `maxSlidesVisible` ; les props voisines sont écrites via dispatch `replace`
 * — pattern InfoCardIllustrationField.
 */
export default function CarouselSlidesVisibleField({ value, onChange }) {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const isLocked = useThemeLockedProp('carousel');

  const maxSlides = !!value;
  const edgeFade = itemProps.edgeFade !== false;
  const scrollAxis = itemProps.scrollAxis === 'vertical' ? 'vertical' : 'horizontal';
  const scrollDirection = itemProps.scrollDirection === 'backward' ? 'backward' : 'forward';

  return (
    <Stack gap={12}>
      {!isLocked('maxSlidesVisible') && (
        <Switch
          size="sm"
          label="Maximum de slides visibles"
          description={maxSlides
            ? 'Chaque slide se dimensionne sur son contenu, et le ruban défile sans arrêt.'
            : 'Un nombre fixe de slides, avec un arrêt sur chacune.'}
          checked={maxSlides}
          onChange={(e) => onChange(e.currentTarget.checked)}
        />
      )}

      {!isLocked('scrollAxis') && (
        <InspectorRow
          label="Axe"
          layout="stacked"
          description={scrollAxis === 'vertical' && (maxSlides
            ? 'Les slides défilent en colonne, chacune à la hauteur réglée plus bas.'
            : 'Les slides s’empilent, chacune à la hauteur réglée plus bas.')}
        >
          <SegmentedControl
            fullWidth
            size="xs"
            color="editor"
            value={scrollAxis}
            onChange={(next) => updateItemProps({ scrollAxis: next })}
            data={[
              { value: 'horizontal', label: 'Horizontal' },
              { value: 'vertical', label: 'Vertical' },
            ]}
          />
        </InspectorRow>
      )}

      {maxSlides ? (
        <>
          {scrollAxis === 'vertical' && (
            <StyleDefaultMark propKey="stripHeight">
              <PxSpacingInput
                value={itemProps.stripHeight ?? ''}
                onChange={(next) => updateItemProps({ stripHeight: next })}
                label="Hauteur visible du ruban"
                description="La portion de la colonne qu'on voit ; les slides défilent à l'intérieur."
                max={1200}
              />
            </StyleDefaultMark>
          )}
          <StyleDefaultMark propKey="scrollDirection">
            <InspectorRow label="Sens du défilement" layout="stacked">
              <SegmentedControl
                fullWidth
                size="xs"
                color="editor"
                value={scrollDirection}
                onChange={(next) => updateItemProps({ scrollDirection: next })}
                data={CAROUSEL_SCROLL_DIRECTIONS[scrollAxis]}
              />
            </InspectorRow>
          </StyleDefaultMark>
          {!isLocked('edgeFade') && (
            <Switch
              size="sm"
              label="Fondu sur les bords"
              description={scrollAxis === 'vertical'
                ? 'Les slides s’effacent en haut et en bas, au lieu d’être coupées net.'
                : 'Les slides s’effacent à gauche et à droite, au lieu d’être coupées net.'}
              checked={edgeFade}
              onChange={(e) => updateItemProps({ edgeFade: e.currentTarget.checked })}
            />
          )}
          {edgeFade && !isLocked('edgeFadeWidth') && (
            <PxSpacingInput
              value={itemProps.edgeFadeWidth ?? ''}
              onChange={(next) => updateItemProps({ edgeFadeWidth: next })}
              label="Largeur du fondu"
              max={200}
            />
          )}
        </>
      ) : !isLocked('slidesVisible') && (
        <NumberInput
          size="xs"
          label="Slides visibles à l'écran"
          description="Chaque slide occupe une fraction égale de la largeur."
          value={itemProps.slidesVisible ?? 1}
          onChange={(next) => updateItemProps({ slidesVisible: Number(next) || 1 })}
          min={1}
          max={CAROUSEL_VISIBLE_MAX}
          step={1}
          clampBehavior="strict"
          allowDecimal={false}
        />
      )}
    </Stack>
  );
}
